import { OpenImgContextProvider } from 'openimg/react'
import {
	data,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useMatches,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { type Route } from './+types/root.ts'
import appleTouchIconAssetUrl from './assets/favicons/apple-touch-icon.png'
import faviconAssetUrl from './assets/favicons/favicon.svg'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import { UserDropdown } from './components/user-dropdown.tsx'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { pipeHeaders } from './utils/headers.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl, getImgSrc } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { useOptionalUser } from './utils/user.ts'

export const links: Route.LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		{
			rel: 'icon',
			href: '/favicon.ico',
			sizes: '48x48',
		},
		{ rel: 'icon', type: 'image/svg+xml', href: faviconAssetUrl },
		{ rel: 'apple-touch-icon', href: appleTouchIconAssetUrl },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: Route.MetaFunction = ({ data }) => {
	return [
		{
			title: data
				? 'East Bloomfield Historical Society'
				: 'Error | East Bloomfield Historical Society',
		},
		{
			name: 'description',
			content:
				'East Bloomfield Historical Society - Preserving the history of East Bloomfield, NY since 1838',
		},
	]
}

export async function loader({ request }: Route.LoaderArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUnique({
						select: {
							id: true,
							name: true,
							username: true,
							displayUsername: true,
							image: { select: { objectKey: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = await honeypot.getInputProps()

	return data(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: Route.HeadersFunction = pipeHeaders

function Document({
	children,
	nonce,
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	env?: Record<string, string | undefined>
}) {
	const allowIndexing = ENV.ALLOW_INDEXING !== 'false'
	return (
		<html lang="en" className="h-full overflow-x-hidden">
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

export function Layout({ children }: { children: React.ReactNode }) {
	// if there was an error running the loader, data could be missing
	const data = useLoaderData<typeof loader | null>()
	const nonce = useNonce()
	return (
		<Document nonce={nonce} env={data?.ENV}>
			{children}
		</Document>
	)
}

const tabs = [
	{ name: 'Home', href: '/' },
	{ name: 'About', href: '/about' },
	{ name: 'Events', href: '/events' },
	{ name: 'Volunteer', href: '/volunteer' },
	{ name: 'Membership', href: '/membership' },
	{ name: 'Records', href: '/records' },
	{ name: 'Store', href: '/store' },
]

function App() {
	const data = useLoaderData<typeof loader>()
	const user = useOptionalUser()
	const matches = useMatches()
	useToast(data.toast)

	const currentPath = matches[1]?.pathname ?? '/'
	const currentTabHref = `/${currentPath.substring(1).split('/')[0]}`

	function handleRouteChange(event: React.ChangeEvent<HTMLSelectElement>) {
		const value = event.target.value
		if (value === 'Log In') {
			window.location.href = '/login'
			return
		}
		if (value === 'Profile') {
			window.location.href = `/users/${user?.username}`
			return
		}
		const tab = tabs.find((t) => t.name === value)
		if (tab) {
			window.location.href = tab.href
		}
	}

	return (
		<OpenImgContextProvider
			optimizerEndpoint="/resources/images"
			getSrc={getImgSrc}
		>
			<div className="ebhs-parchment-bg flex min-h-screen flex-col justify-between">
				<nav className="pt-8">
					<div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
						<div className="mb-6 text-center text-3xl">
							<Link to="/">
								<img
									src="/images/ebhs/academy.jpg"
									alt="Academy building"
									className="mb-3 inline max-w-xs sm:max-w-sm"
								/>
								<br />
								East Bloomfield Historical Society
							</Link>
						</div>
					</div>
				</nav>

				<header>
					<div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
						<div className="sm:hidden">
							<label htmlFor="tabs" className="sr-only">
								Select a tab
							</label>
							<select
								id="tabs"
								name="tabs"
								className="block w-full rounded-md border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
								onChange={handleRouteChange}
								defaultValue={
									tabs.find((tab) => tab.href === currentTabHref)
										?.name ?? 'Home'
								}
							>
								{tabs.map((tab) => (
									<option key={tab.name}>{tab.name}</option>
								))}
								{user ? (
									<option>Profile</option>
								) : (
									<option>Log In</option>
								)}
							</select>
						</div>
						<div className="hidden sm:block">
							<nav
								className="relative z-0 flex divide-x divide-gray-200 rounded-lg shadow"
								aria-label="Tabs"
							>
								{tabs.map((tab, tabIdx) => (
									<Link
										key={tab.name}
										to={tab.href}
										className={[
											tab.href === currentTabHref
												? 'text-gray-900'
												: 'text-gray-500 hover:text-gray-700',
											tabIdx === 0 ? 'rounded-l-lg' : '',
											'group relative min-w-0 flex-1 overflow-hidden bg-white px-4 py-4 text-center text-sm font-medium hover:bg-gray-50 focus:z-10',
										].join(' ')}
										aria-current={
											tab.href === currentTabHref ? 'page' : undefined
										}
									>
										<span>{tab.name}</span>
										<span
											aria-hidden="true"
											className={[
												tab.href === currentTabHref
													? 'bg-indigo-500'
													: 'bg-transparent',
												'absolute inset-x-0 bottom-0 h-0.5',
											].join(' ')}
										/>
									</Link>
								))}
								{user ? (
									<UserDropdown />
								) : (
									<Link
										to="/login"
										className="group relative min-w-0 flex-1 overflow-hidden rounded-r-lg bg-white px-4 py-4 text-center text-sm font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-700 focus:z-10"
									>
										<span>Log In</span>
									</Link>
								)}
							</nav>
						</div>
					</div>
				</header>

				<div className="flex flex-1 flex-col">
					<Outlet />
				</div>

				<footer className="bg-white">
					<div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8 lg:py-6">
						<div className="md:flex md:items-center md:justify-between">
							<div className="flex md:order-3">
								<a
									href="https://www.facebook.com/East-Bloomfield-Historical-Society-233421727292/"
									className="text-[#4267b2] hover:text-blue-700"
									target="_blank"
									rel="noreferrer"
								>
									<span className="sr-only">Facebook</span>
									<svg
										className="h-20 w-20"
										fill="currentColor"
										viewBox="0 0 24 24"
									>
										<path
											fillRule="evenodd"
											d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
											clipRule="evenodd"
										/>
									</svg>
								</a>
							</div>
							<p className="mt-8 text-lg leading-6 text-gray-400 md:order-2 md:mt-0">
								&copy; {new Date().getFullYear()}{' '}
								<Link to="/" className="underline">
									East Bloomfield Historical Society
								</Link>
								.<br />
								Mailing Address: PO Box 212, East Bloomfield, NY 14443
								<br />
								Museum Address: 8 South Ave, Bloomfield, NY 14469
								<br />
								Phone:{' '}
								<a href="tel:5856577244" className="text-black">
									585-657-7244
								</a>
								<br />
								<br />
								April-November Hours: (By Appointment) W/Th/F 9-2.
								<br />
								December-March Hours: (By Appointment) Thursday 10-2.
							</p>
							<img
								src="/images/ebhs/logo.png"
								alt="Logo featuring a sketch of the academy museum"
								className="w-40 opacity-75 md:order-1"
							/>
						</div>
					</div>
				</footer>
			</div>
			<EpicToaster closeButton position="top-center" />
			<EpicProgress />
		</OpenImgContextProvider>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default AppWithProviders

// this is a last resort error boundary. There's not much useful information we
// can offer at this level.
export const ErrorBoundary = GeneralErrorBoundary
